<!--
	@name app-page
	@description Generic Page
	@date 2020/05/20
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<div>
			<v-container id="fixedHeight">
				<div class="pb-8">
					<div id="background" />

					<v-container style="max-width: 950px; position: relative; z-index: 4">
						<v-sheet class="pa-14 mt-12">
							<div v-if="loadingPageItem">
								<v-skeleton-loader type="heading" />
								<v-skeleton-loader type="text" class="mt-4" />
								<v-skeleton-loader type="image" class="mt-4" />
								<v-skeleton-loader class="mt-4 mx-n4" type="list-item-three-line@3" />
							</div>

							<div v-else>
								<div class="text-h4 font-weight-bold mb-0">
									{{ page?.name }}
								</div>

								<v-divider inset class="my-8" />

								<!-- html content here -->
								<div class="main--copy" v-html="$sanitize(page?.story, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'] })" />

								<v-btn v-if="page.ctaText" class="mt-8" color="primary" :href="page.ctaDestination" target="_blank">
									{{ page.ctaText }}
								</v-btn>
							</div>
						</v-sheet>
					</v-container>
				</div>
			</v-container>
		</div>

		<common-form-contact v-if="showContactForm" :campaign="`Page: ${page.id}`">
			<template v-if="page?.contactFormCopy" #copy>
				<slot>
					<div class="white--text text-center" v-html="$sanitize(page?.contactFormCopy)" />
				</slot>
			</template>
		</common-form-contact>
	</div>
</template>

<script>
	import { mapActions } from 'vuex';
	import { ElementTools } from '@/utils';
	import CommonFormContact from '@/component/common/form/contact';

	export default {
		name: 'app-page',

		components: {
            CommonFormContact
        },

		data() {
			return {
				page: null,
				loadingPageItem: true,
                showContactForm: false
			};
		},

		computed: {
			url() {
				return encodeURIComponent(window.location.href);
			}
		},

		watch: {
			'$route.params.id'() {
				this.init();
			}
		},

		created() {
			this.init();
        },

        beforeDestroy() {
            window.zE('webWidget', 'show');
            this.setFooter({ links: true });
            this.setHeader({ links: true });
		},

		methods: {
			...mapActions('CmsPage', ['loadPageItem']),
			...mapActions('Layout', ['setFooter', 'setHeader']),

			init() {
				this.loadingPageItem = true;

				this.loadPageItem(this.$route.params.id).then(({ data }) => {
					this.page = data;
					this.loadingPageItem = false;
                    if(!this.page.displayChat) window.zE('webWidget', 'hide');
                    if(this.page.displayContactForm) this.showContactForm = true;
                    if(!this.page.displayFooter) this.setFooter({ links: false });
                    if(!this.page.displayHeader) this.setHeader({ links: false });
				});
			},

			copyLink() {
				navigator.clipboard
					.writeText(window.location.href)
					.then(() => ElementTools.fireNotification(this.$el, 'success', `Link copied to clipboard`))
					.catch(() => ElementTools.fireNotification(this.$el, 'error', `Failed to copy link to clipboard`));
			}
		}
	};
</script>

<style lang="scss" scoped>
    .main--copy ::v-deep h2 {
        margin-bottom: 12px;
    }

    .main--copy ::v-deep h3 {
        margin-bottom: 12px;
    }

    .main--copy ::v-deep {
        p, li {
            font-size: 1rem;
            line-height: 1.5rem;
            letter-spacing: 0.03125em !important;
            color: rgba(0, 0, 0, 0.7);
        }
        p, ul {
            margin-bottom: 12px;
        }
        li {
            margin-bottom: 4px;
        }
    }

    .main--copy ::v-deep :last-child {
        margin-bottom: 0 !important;
    }

    #fixedHeight {
        min-height: 600px;
    }

	#background {
		background-image: linear-gradient(to right, #470048, #1b78d8);
		width: 100%;
		height: 600px;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		z-index: 1;
	}

	.line-clamp {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
</style>
